import type { Product } from '@/services/products'
import type { NormalizedProduct } from '@/core/product'
import { computeDiscount } from '@/lib/compareAtPrice'

export type ProductBadge = 'new' | 'hot' | 'digital' | 'service' | 'dropship' | 'sale'

/** True when the catalog row represents an unavailable purchasable (non-dropship) product. */
export function isShopProductOutOfStock(product: Product): boolean {
  if (product.is_dropshipping) return false
  if (product.status === 'out_of_stock') return true
  if (product.track_inventory === false) return false
  const total = product.total_stock ?? product.stock
  return typeof total === 'number' && Number.isFinite(total) && total <= 0
}

/** Display-only badges derived from product fields (no API changes). */
export function getShopProductBadge(product: Product): ProductBadge | null {
  // Type-based badges take priority over recency/popularity
  if (product.product_type === 'digital' || product.digital_file_url) return 'digital'
  if (product.product_type === 'service') return 'service'
  if (product.is_dropshipping) return 'dropship'
  if (product.has_variants && product.variants?.length) {
    for (const v of product.variants) {
      const p = Number(v.price ?? 0)
      const c = v.compare_at_price != null ? Number(v.compare_at_price) : null
      if (c != null && Number.isFinite(c) && computeDiscount(p, c) != null) return 'sale'
    }
  } else {
    const compareAt = product.compare_at_price != null ? Number(product.compare_at_price) : null
    if (compareAt != null && computeDiscount(Number(product.price), compareAt) != null) return 'sale'
  }
  const created = new Date(product.created_at).getTime()
  if (!Number.isFinite(created)) return product.orders_count >= 10 ? 'hot' : null
  const daysSince = (Date.now() - created) / 86_400_000
  if (daysSince >= 0 && daysSince <= 21) return 'new'
  if (product.orders_count >= 10) return 'hot'
  return null
}

export type NormalizedBadge = 'digital' | 'service' | 'dropship' | 'sale' | 'new' | 'hot'

/** Badges for the runtime storefront ProductCard — from NormalizedProduct. */
export function getNormalizedProductBadge(product: NormalizedProduct): NormalizedBadge | null {
  if (product.isDigital) return 'digital'
  if (product.isService) return 'service'
  if (product.isDropshipping) return 'dropship'
  if (
    product.compareAtPrice != null &&
    computeDiscount(product.price, product.compareAtPrice) != null
  )
    return 'sale'
  return null
}

export const NORMALIZED_BADGE_CONFIG: Record<NormalizedBadge, { label: string; className: string }> = {
  digital: { label: 'Numérique', className: 'bg-primary/15 text-white' },
  service: { label: 'Service', className: 'bg-blue-600 text-white' },
  dropship: { label: 'Import', className: 'bg-amber-500 text-white' },
  sale: { label: 'Promo', className: 'bg-red-600 text-white' },
  new: { label: 'Nouveau', className: 'bg-emerald-600 text-white' },
  hot: { label: 'Populaire', className: 'bg-orange-500 text-white' },
}
